/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {BaseDB_ApiGeneratorCaller} from "@intuitionrobotics/db-api-generator/frontend";
import {ThunderDispatcher, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {DB_User, getUserNameWithTitle} from "@app/ir-q-app-common/types/care-center/user";
import {getUserOrgRoles, upsertImpl} from "@modules/utils";
import {AccountModule, StorageKey_UserEmail} from "@intuitionrobotics/user-account/frontend";
import {HttpMethod} from "@intuitionrobotics/thunderstorm";
import {PartialProperties} from "@intuitionrobotics/ts-common";
import {KasperAccountModule} from "./KasperAccountModule";
import {DB_Patient} from "@app/app-shared/patient";
import {AccountApi_RefreshJWT, ApiUserChangePassword, ApiUserUnsubscribe} from "@app/app-shared/api";
import {Request_UpsertAccount} from "@intuitionrobotics/user-account";
import {ID_ALL, OrgRoleLevel, PushKey_PermissionUpdate} from "@app/app-shared/permission";
import {ORGANIZATION_TYPE} from "@app/ir-q-app-common/types/care-center/organization";
import {GroupType} from "@app/ir-q-app-common/types/contacts";
import {PushPubSubModule} from "@intuitionrobotics/push-pub-sub/app-frontend/modules/PushPubSubModule";
import {OrganizationMap} from "../app/pages/ui/organizations/OrganizationsContext";


export interface OnUsersUpdated {
    __onUsersUpdated: () => void;
}

export const dispatch_onUsersUpdated = new ThunderDispatcher<OnUsersUpdated, "__onUsersUpdated">("__onUsersUpdated");

export class UsersModule_Class
    extends BaseDB_ApiGeneratorCaller<DB_User> {

    private users?: DB_User[];

    getUsers = () => this.users || [];

    getByMail = (email?: string): DB_User | undefined => {
        if (!email)
            return;

        if (!this.users)
            return;

        const user = this.users.find(p => p.mail === email);
        if (user)
            return user;

        console.group("UsersModule.getByMail")
        this.logError(`This should never happen`);
        this.logError(`Email ${email} and users are ${this.users.map(u => u.mail).join()}`);
        console.groupEnd()
    };

    createWithKasperAccount(toCreate: PartialProperties<DB_User, "_id">, password?: string, password_check?: string) {
        return KasperAccountModule.create({user: toCreate, email: toCreate.mail, password, password_check}, async (user: DB_User) => {
            await this.onEntryCreated(user);
        });
    }

    changePassword(body: Request_UpsertAccount) {
        XhrHttpModule
            .createRequest<ApiUserChangePassword>(HttpMethod.POST, "update-password")
            .setRelativeUrl("/v1/user/change-password")
            .setJsonBody(body)
            .setOnError(e => {
                this.logError("Failed to change password", e);
                this.dispatchUsersUpdated();
            })
            .execute(() => {
                this.dispatchUsersUpdated()
            })
    }

    getSenderTitle = (senderId: string) => {
        const user = this.users?.find(p => p._id === senderId);
        if (!user)
            return;
        return getUserNameWithTitle(user);
    };

    getCurrentUser = () => {
        const user = this.getCurrentUserByEmail();
        return user && getUserNameWithTitle(user);
    };

    getCurrentUserByEmail = () => {
        const email = StorageKey_UserEmail.get();
        return this.getByMail(email);
    };

    getCurrentUserAccount = () => {
        const email = StorageKey_UserEmail.get();
        return AccountModule.getAccounts().find(_account => _account.email === email);
    };

    async onEntryCreated(response: DB_User): Promise<void> {
        this.upsertImpl(response);
        AccountModule.listUsers();
        this.dispatchUsersUpdated();
    }

    async onEntryUpdated(response: DB_User): Promise<void> {
        this.upsertImpl(response);
        this.dispatchUsersUpdated();
    }

    async onEntryDeleted(response: DB_User): Promise<void> {
    }

    async onGotUnique(response: DB_User): Promise<void> {
    }

    async onQueryReturned(response: DB_User[]): Promise<void> {
        this.users = this.users || [];
        response.reduce((carry, el) => upsertImpl(carry, el), this.users);
        this.dispatchUsersUpdated();

        const currentUser = this.getCurrentUserByEmail();
        if (currentUser?._id) {
            PushPubSubModule.subscribe({
                pushKey: PushKey_PermissionUpdate,
                props: {userId: currentUser._id},
                callBack: (data) => {
                    XhrHttpModule
                        .createRequest<AccountApi_RefreshJWT>(HttpMethod.POST, "refresh-jwt")
                        .setRelativeUrl("/v1/account/refresh-jwt")
                        .setOnError((err) => console.error("error regenerating jwt", err))
                        .execute();
                }
            });
        }
    }

    private dispatchUsersUpdated = () => {
        dispatch_onUsersUpdated.dispatchUI();
        dispatch_onUsersUpdated.dispatchModule();
    };

    private upsertImpl = (item: DB_User) => {
        this.users = this.users || [];
        return upsertImpl(this.users, item);
    };

    getUser = (id?: string): DB_User | undefined => this.users?.find(user => user._id === id);

    getNeedsGroupSelection(_orgIds: string[], orgMap: OrganizationMap, preselectedPatient?: DB_Patient): { needsGroupSelection: boolean; availableGroupTypes: Set<GroupType>; groupContactIds: any[] } {
        const orgRoles = getUserOrgRoles();

        const orgIds = preselectedPatient ? _orgIds.filter(org => org === preselectedPatient.organizationId) : _orgIds;

        let hasCSMessage = false;
        let hasClOrExMessage = false;

        const availableGroupTypes = new Set<GroupType>()
        const groupContactIds = [];
        for (const oId of orgIds) {
            const organization = orgMap[oId];
            if (!organization)
                continue;

            const roles = orgRoles[ID_ALL] || orgRoles[oId];
            if (!roles)
                continue

            if (roles.includes(OrgRoleLevel.CS_MESSAGE)) {
                hasCSMessage = true;
                availableGroupTypes.add(GroupType.CUSTOMER_SERVICE)
                groupContactIds.push(organization.groupContactIds[0])
            }
            if (organization.type === ORGANIZATION_TYPE.CLINICAL && roles.includes(OrgRoleLevel.CL_MESSAGE)) {
                hasClOrExMessage = true;
                availableGroupTypes.add(GroupType.CLINIC)
                groupContactIds.push(organization.groupContactIds[1])
            }
            if (organization.type === ORGANIZATION_TYPE.EXTERNAL && roles.includes(OrgRoleLevel.EX_MESSAGE)) {
                hasClOrExMessage = true;
                availableGroupTypes.add(GroupType.EXTERNAL)
                groupContactIds.push(organization.groupContactIds[2])
            }
            if (organization.type === ORGANIZATION_TYPE.MULTI_CONTACT && roles.includes(OrgRoleLevel.CL_MESSAGE)) {
                hasClOrExMessage = true;
                availableGroupTypes.add(GroupType.CLINIC)
                groupContactIds.push(organization.groupContactIds[1])
            }
            if (organization.type === ORGANIZATION_TYPE.MULTI_CONTACT && roles.includes(OrgRoleLevel.EX_MESSAGE)) {
                hasClOrExMessage = true;
                availableGroupTypes.add(GroupType.EXTERNAL)
                groupContactIds.push(organization.groupContactIds[2])
            }
        }

        return {needsGroupSelection: hasCSMessage && hasClOrExMessage, groupContactIds, availableGroupTypes};
    }

    unsubscribe = async () => {
        XhrHttpModule
            .createRequest<ApiUserUnsubscribe>(HttpMethod.GET, "user-unsubscribe")
            .setRelativeUrl("/v1/user/unsubscribe")
            .setOnError("Failed to unsubscribe user")
            .execute()
    }

    // updateUsersPermissions(permission: PartialProperties<DB_Permission, "_id">, userIds: string[]) {
    // 	this.users?.forEach(u => {
    // 		if (!userIds.includes(u._id))
    // 			return;
    //
    // 		u.permissionId = permission._id || AppPermissionsModule.getItems().find(p => p.name === permission.name)?._id;
    // 	})
    //
    // 	dispatch_onUsersUpdated.dispatchUI()
    //
    // }
}

export const UsersModule = new UsersModule_Class({key: "user", relativeUrl: "/v1/user"}, "UsersModule");
